/** Действия над стором: всё обращение к API и загрузкам собрано здесь. */
import { useCallback, useMemo, useRef } from 'react';
import { DEFAULT_SETTINGS, LIMITS } from '@reels/shared/constants';
import type { ProjectSettings } from '@reels/shared';
import { api, ApiError, errorMessage } from '../lib/api';
import { markOnboardingSeen } from '../lib/prefs';
import * as telegram from '../lib/telegram';
import { checkFile, detectKind } from '../lib/video';
import { uploadAsset, uploadErrorMessage, UploadAbortedError, resumableState } from '../lib/upload';
import type { EdlPatch, ProjectDetail } from '../lib/types';
import { parseStartParam } from './screen';
import { useStore } from './store';
import { t } from '../i18n';
import type { UploadItem } from './store';

/** Контроллеры активных загрузок: живут вне стора, чтобы не сериализовать их. */
const controllers = new Map<string, AbortController>();
/** Файлы держим отдельно: File в сторе делал бы состояние несериализуемым. */
const files = new Map<string, File>();

/**
 * Два файла одновременно на все пачки вместе; внутри каждого уже ограничено число
 * multipart-запросов. Очередь общая, чтобы файлы, добавленные во время загрузки,
 * встали в неё, а не отбрасывались.
 */
const UPLOAD_CONCURRENCY = 2;
let activeUploads = 0;
const waitingUploads: Array<() => void> = [];

async function withUploadSlot(job: () => Promise<void>): Promise<void> {
  if (activeUploads < UPLOAD_CONCURRENCY) activeUploads += 1;
  else await new Promise<void>(resolve => waitingUploads.push(resolve));
  try { await job(); }
  finally {
    // Место передаём следующему в очереди, не освобождая его.
    const next = waitingUploads.shift();
    if (next) next(); else activeUploads -= 1;
  }
}

let localId = 0;
function nextId(): string {
  localId += 1;
  return `u${localId}-${Date.now().toString(36)}`;
}

export interface Actions {
  bootstrap: () => Promise<void>;
  refreshProjects: () => Promise<void>;
  /** Профиль: счётчики генераций и ссылки поддержки. */
  loadProfile: () => Promise<void>;
  /** Онбординг пройден или пропущен: открыть редактор и запомнить это у пользователя. */
  finishOnboarding: () => void;
  openProject: (id: string) => Promise<void>;
  reloadProject: () => Promise<void>;
  /** Черновик для загрузки: текущий или новый. Настройки, выбранные до него, уходят на сервер. */
  ensureProject: () => Promise<ProjectDetail | null>;
  renameProject: (id: string, title: string) => Promise<boolean>;
  deleteProject: (id: string) => Promise<void>;
  addFiles: (list: FileList | File[], slot: 'reaction' | 'illustration') => Promise<void>;
  moveSource: (assetId: string, delta: number) => Promise<void>;
  retryUpload: (id: string) => Promise<void>;
  removeUpload: (id: string) => Promise<void>;
  /** Удалить уже загруженный ранее файл проекта (черновик открыли заново). */
  removeAsset: (assetId: string) => Promise<void>;
  saveSettings: (settings: ProjectSettings) => Promise<void>;
  startGeneration: () => Promise<void>;
  applyEdlPatch: (patch: EdlPatch, options?: { rerender?: boolean }) => Promise<boolean>;
  rerenderCurrent: () => Promise<boolean>;
  /** Новый план монтажа готового проекта, а не пересборка старой нарезки. */
  remontage: () => Promise<boolean>;
  deliver: () => Promise<void>;
  toast: (message: string | null) => void;
}

export function useActions(): Actions {
  const { state, dispatch } = useStore();
  const addingSlots = useRef(new Set<'reaction' | 'illustration'>());

  const toast = useCallback(
    (message: string | null) => dispatch({ type: 'toast', message }),
    [dispatch],
  );

  const refreshProjects = useCallback(async () => {
    dispatch({ type: 'projects/loading' });
    try {
      const response = await api.listProjects();
      dispatch({ type: 'projects/loaded', items: response.items ?? [] });
    } catch (error) {
      dispatch({ type: 'projects/error', message: errorMessage(error) });
    }
  }, [dispatch]);

  const loadProfile = useCallback(async () => {
    dispatch({ type: 'profile/loading' });
    try {
      const profile = await api.me();
      dispatch({ type: 'profile/loaded', profile });
    } catch (error) {
      dispatch({ type: 'profile/error', message: errorMessage(error) });
    }
  }, [dispatch]);

  const finishOnboarding = useCallback(() => {
    markOnboardingSeen();
    dispatch({ type: 'onboarding/seen' });
    dispatch({ type: 'route', screen: 'editor' });
    // Не ждём ответа: если сервер недоступен, онбординг просто покажется ещё раз.
    void api.markOnboardingSeen().catch(() => undefined);
  }, [dispatch]);

  const openProject = useCallback(
    async (id: string) => {
      dispatch({ type: 'project/loading', value: true });
      try {
        const project = await api.getProject(id);
        dispatch({ type: 'project/set', project: normalize(project) });
        if (project.status === 'done') dispatch({ type: 'activity/read', id });
        dispatch({ type: 'route', screen: null });
        dispatch({ type: 'uploads/clear' });
      } catch (error) {
        dispatch({ type: 'project/loading', value: false });
        toast(errorMessage(error));
      }
    },
    [dispatch, toast],
  );

  const reloadProject = useCallback(async () => {
    const current = state.project;
    if (!current) return;
    try {
      const project = await api.getProject(current.id);
      dispatch({ type: 'project/set', project: normalize(project) });
    } catch (error) {
      toast(errorMessage(error));
    }
  }, [dispatch, state.project, toast]);

  const bootstrap = useCallback(async () => {
    dispatch({ type: 'boot/start' });
    try {
      const auth = await api.authenticate();
      dispatch({ type: 'boot/success', user: auth.user });
    } catch (error) {
      dispatch({ type: 'boot/error', message: errorMessage(error) });
      return;
    }
    const target = parseStartParam(telegram.startParam);
    if (target && 'projectId' in target) await openProject(target.projectId);
    else if (target) dispatch({ type: 'route', screen: target.screen });
    await refreshProjects();
  }, [dispatch, openProject, refreshProjects]);

  const ensureProject = useCallback(async (): Promise<ProjectDetail | null> => {
    if (state.project?.status === 'draft') return state.project;
    dispatch({ type: 'busy', value: true });
    try {
      const created = await api.createProject('reaction');
      // Настройки выбраны до появления проекта: отправляем сразу, иначе ответ
      // сервера перекрыл бы их значениями по умолчанию.
      let settings = state.settings;
      try {
        settings = (await api.updateSettings(created.id, state.settings)).settings;
      } catch {
        // сохранятся перед генерацией
      }
      const project = normalize({
        ...created,
        settings,
        assets: [],
        edl: null,
        edlVersion: null,
        candidates: [],
        render: null,
      });
      dispatch({ type: 'project/created', project });
      return project;
    } catch (error) {
      toast(errorMessage(error));
      return null;
    } finally {
      dispatch({ type: 'busy', value: false });
    }
  }, [dispatch, state.project, state.settings, toast]);

  const renameProject = useCallback(async (id: string, title: string): Promise<boolean> => {
    try {
      const renamed = await api.renameProject(id, title);
      if (state.project?.id === id) dispatch({ type: 'project/set', project: { ...state.project, title: renamed.title } });
      await refreshProjects();
      return true;
    } catch (error) {
      toast(errorMessage(error));
      return false;
    }
  }, [dispatch, refreshProjects, state.project, toast]);

  const deleteProject = useCallback(
    async (id: string) => {
      try {
        await api.deleteProject(id);
        dispatch({ type: 'activity/remove', id });
        if (state.project?.id === id) dispatch({ type: 'project/set', project: null });
        await refreshProjects();
      } catch (error) {
        toast(errorMessage(error));
      }
    },
    [dispatch, refreshProjects, state.project, toast],
  );

  const runUpload = useCallback(
    async (item: UploadItem, project: ProjectDetail) => {
      const file = files.get(item.id);
      if (!file) return;
      const controller = new AbortController();
      controllers.set(item.id, controller);
      dispatch({ type: 'uploads/update', id: item.id, patch: { status: 'uploading', error: null } });
      telegram.enableClosingConfirmation();
      try {
        const result = await uploadAsset({
          projectId: project.id,
          file,
          kind: item.kind,
          durationS: item.durationS,
          position: item.position,
          onRegistered: assetId => dispatch({ type: 'uploads/update', id: item.id, patch: { assetId } }),
          signal: controller.signal,
          onProgress: (progress) => {
            dispatch({
              type: 'uploads/update',
              id: item.id,
              patch: {
                progress: progress.ratio,
                uploadedBytes: progress.uploaded,
                resumed: progress.resumed,
              },
            });
          },
        });
        dispatch({
          type: 'uploads/update',
          id: item.id,
          patch: { status: 'done', progress: 1, assetId: result.assetId, error: null },
        });
        telegram.hapticFeedback.notification('success');
      } catch (error) {
        if (error instanceof UploadAbortedError) {
          dispatch({ type: 'uploads/update', id: item.id, patch: { status: 'canceled' } });
        } else {
          dispatch({
            type: 'uploads/update',
            id: item.id,
            patch: { status: 'error', error: uploadErrorMessage(error) },
          });
          telegram.hapticFeedback.notification('error');
        }
      } finally {
        controllers.delete(item.id);
        if (!controllers.size) telegram.disableClosingConfirmation();
      }
    },
    [dispatch],
  );

  const addFiles = useCallback(
    async (list: FileList | File[], slot: 'reaction' | 'illustration') => {
      const incoming = Array.from(list);
      if (!incoming.length) return;
      // Блокировка только на подготовку: пока карточки не в сторе, лимиты считались бы без них.
      if (addingSlots.current.has(slot)) { toast(t('lib.actions.wait_adding')); return; }
      addingSlots.current.add(slot);
      let items: UploadItem[];
      let project: ProjectDetail;
      try {
        // Иллюстрации к готовому ролику грузятся в тот же проект: их подхватит новый монтаж.
        const current = state.project;
        const reuse = current && (current.status === 'draft' || (slot === 'illustration' && current.status === 'done'));
        const draft = reuse ? current : await ensureProject();
        if (!draft) return;
        project = draft;
        const uploadIds = new Set(state.uploads.map(u => u.assetId));
        const existing = project.assets.filter(a => !uploadIds.has(a.id));
        const countIncoming = incoming.filter(file => !resumableState(project.id, file)).length;
        const same = state.uploads.filter(u => u.slot === slot).length + existing.filter(a => (a.kind === 'reaction') === (slot === 'reaction')).length;
        const limit = slot === 'reaction' ? LIMITS.sources.maxCount : LIMITS.maxIllustrations;
        if (same + countIncoming > limit) { toast(t('lib.actions.can_add_more', { n: Math.max(0, limit - same) })); return; }
        const sourceBytes = existing.filter(a => a.kind === 'reaction').reduce((n, a) => n + (a.bytes ?? 0), 0)
          + state.uploads.filter(u => u.slot === 'reaction').reduce((n, u) => n + u.bytes, 0);
        if (slot === 'reaction' && sourceBytes + incoming.filter(file => !resumableState(project.id, file)).reduce((n, f) => n + f.size, 0) > LIMITS.sources.maxBytes) {
          toast(t('lib.actions.total_size')); return;
        }
        const firstPosition = Math.max(-1, ...existing.map(a => a.position ?? 0), ...state.uploads.map(u => u.position ?? 0)) + 1;
        items = incoming.map((file, index) => {
          const id = nextId(); files.set(id, file);
          const resumed = resumableState(project.id, file);
          const previous = existing.find(a => a.id === resumed?.assetId);
          return { id, slot, kind: detectKind(file, slot), name: file.name, bytes: file.size, position: previous?.position ?? firstPosition + index,
            thumbUrl: URL.createObjectURL(file), status: 'checking', progress: 0, uploadedBytes: 0, durationS: null, width: null, height: null, assetId: resumed?.assetId ?? null, error: null, resumed: false };
        });
        dispatch({ type: 'uploads/add', items });
      } finally { addingSlots.current.delete(slot); }

      await Promise.allSettled(items.map(item => withUploadSlot(async () => {
        const file = files.get(item.id);
        if (!file) return;
        let check;
        try { check = await checkFile(file, slot); }
        catch (error) { dispatch({ type: 'uploads/update', id: item.id, patch: { status: 'error', error: errorMessage(error) } }); return; }
        const patch: Partial<UploadItem> = { kind: check.kind, durationS: check.info.durationS, width: check.info.width, height: check.info.height };
        if (!files.has(item.id)) return;
        if (check.error) {
          dispatch({ type: 'uploads/update', id: item.id, patch: { ...patch, status: 'error', error: check.error } });
          return;
        }
        dispatch({ type: 'uploads/update', id: item.id, patch: { ...patch, status: 'queued' } });
        await runUpload({ ...item, ...patch, status: 'queued' }, project);
      })));
    },
    [dispatch, ensureProject, runUpload, state.project, state.uploads, toast],
  );

  const retryUpload = useCallback(
    async (id: string) => {
      const item = state.uploads.find((entry) => entry.id === id);
      const project = state.project;
      if (!item || !project) return;
      await runUpload(item, project);
    },
    [runUpload, state.project, state.uploads],
  );

  const removeUpload = useCallback(
    async (id: string) => {
      const item = state.uploads.find((entry) => entry.id === id);
      controllers.get(id)?.abort();
      if (item?.assetId) {
        try { await api.deleteAsset(item.assetId); }
        catch (error) { toast(errorMessage(error)); return; }
      }
      controllers.delete(id);
      files.delete(id);
      if (item?.thumbUrl) URL.revokeObjectURL(item.thumbUrl);
      dispatch({ type: 'uploads/remove', id });
      // Иначе удалённый файл всплывёт строкой «загружен ранее» из списка проекта.
      const project = state.project;
      if (item?.assetId && project?.assets.some(asset => asset.id === item.assetId)) {
        dispatch({ type: 'project/merge', patch: { assets: project.assets.filter(asset => asset.id !== item.assetId) } });
      }
    },
    [dispatch, state.project, state.uploads, toast],
  );

  const removeAsset = useCallback(
    async (assetId: string) => {
      try {
        await api.deleteAsset(assetId);
        const project = state.project;
        if (project) {
          dispatch({
            type: 'project/merge',
            patch: { assets: project.assets.filter((asset) => asset.id !== assetId) },
          });
        }
      } catch (error) {
        toast(errorMessage(error));
      }
    },
    [dispatch, state.project, toast],
  );

  const moveSource = useCallback(async (assetId: string, delta: number) => {
    const project = state.project;
    if (!project || state.uploads.some(u => u.status !== 'done')) return;
    const uploadIds = new Set(state.uploads.map(u => u.assetId));
    const rows = [
      ...project.assets.filter(a => a.kind === 'reaction' && !uploadIds.has(a.id)).map(a => ({ id: a.id, position: a.position ?? 0 })),
      ...state.uploads.filter(u => u.slot === 'reaction' && u.assetId).map(u => ({ id: u.assetId!, position: u.position ?? 0 })),
    ].sort((a, b) => a.position - b.position);
    const from = rows.findIndex(a => a.id === assetId), to = from + delta;
    if (from < 0 || to < 0 || to >= rows.length) return;
    [rows[from], rows[to]] = [rows[to]!, rows[from]!];
    dispatch({ type: 'busy', value: true });
    try {
      await api.reorderSources(project.id, rows.map(a => a.id));
      const position = new Map(rows.map((a, i) => [a.id, i]));
      dispatch({ type: 'project/merge', patch: { assets: project.assets.map(a => ({ ...a, position: position.get(a.id) ?? a.position })) } });
      for (const u of state.uploads) if (u.assetId && position.has(u.assetId)) dispatch({ type: 'uploads/update', id: u.id, patch: { position: position.get(u.assetId)! } });
    } catch (error) { toast(errorMessage(error)); }
    finally { dispatch({ type: 'busy', value: false }); }
  }, [dispatch, state.project, state.uploads, toast]);

  const saveSettings = useCallback(
    async (settings: ProjectSettings) => {
      dispatch({ type: 'settings/set', settings });
      const project = state.project;
      if (!project) return;
      try {
        await api.updateSettings(project.id, settings);
      } catch (error) {
        toast(errorMessage(error));
      }
    },
    [dispatch, state.project, toast],
  );

  const startGeneration = useCallback(async () => {
    const project = state.project;
    if (!project) return;
    dispatch({ type: 'busy', value: true });
    try {
      const billing = await api.billing();
      if (billing.available === 0) {
        void loadProfile();
        dispatch({ type: 'route', screen: 'limit' });
        return;
      }
      if (state.uploads.some(u => u.status !== 'done')) throw new Error(t('lib.actions.wait_uploads'));
      await api.updateSettings(project.id, state.settings);
      // Бот пишет только тем, кто дал разрешение: спрашиваем до постановки задач.
      await telegram.requestWriteAccess();
      await api.generate(project.id);
      void loadProfile();
      dispatch({ type: 'route', screen: state.route === 'editor' || project.status === 'draft' ? 'editor' : null });
      dispatch({ type: 'activity/update', id: project.id, patch: { status: 'uploading', progress: 0, error: null } });
      dispatch({ type: 'stage/set', stage: { status: 'uploading', stage: 'uploading', progress: 0 } });
      telegram.hapticFeedback.impact('medium');
    } catch (error) {
      toast(errorMessage(error));
      if (error instanceof ApiError && error.code === 'insufficient_balance') dispatch({ type: 'route', screen: 'limit' });
    } finally {
      dispatch({ type: 'busy', value: false });
    }
  }, [dispatch, state.project, state.route, state.settings, state.uploads, toast, loadProfile]);

  const applyEdlPatch = useCallback(
    async (patch: EdlPatch, options?: { rerender?: boolean }) => {
      const project = state.project;
      if (!project) return false;
      dispatch({ type: 'busy', value: true });
      try {
        const response = await api.patchEdl(project.id, { ...patch, expectedVersion: project.edlVersion ?? undefined });
        if (response.settings) {
          dispatch({ type: 'settings/set', settings: { ...DEFAULT_SETTINGS, ...response.settings } });
          dispatch({ type: 'project/merge', patch: { settings: response.settings } });
        }
        dispatch({ type: 'project/merge', patch: { edl: response.edl, edlVersion: response.version } });
        if (options?.rerender !== false) {
          await api.rerender(project.id, response.version);
          dispatch({ type: 'route', screen: null });
          dispatch({ type: 'activity/update', id: project.id, patch: { status: 'rendering', progress: 0, error: null } });
          dispatch({ type: 'stage/set', stage: { status: 'rendering', stage: 'render', progress: 0 } });
        }
        telegram.hapticFeedback.notification('success');
        return true;
      } catch (error) {
        toast(errorMessage(error));
        if (error instanceof ApiError && error.code === 'insufficient_balance') dispatch({ type: 'route', screen: 'limit' });
        return false;
      } finally {
        dispatch({ type: 'busy', value: false });
      }
    },
    [dispatch, state.project, toast],
  );

  const rerenderCurrent = useCallback(async (): Promise<boolean> => {
    const project = state.project;
    if (!project) return false;
    dispatch({ type: 'busy', value: true });
    try {
      await api.rerender(project.id, project.edlVersion!);
      dispatch({ type: 'route', screen: null });
      dispatch({ type: 'activity/update', id: project.id, patch: { status: 'rendering', progress: 0, error: null } });
      dispatch({ type: 'stage/set', stage: { status: 'rendering', stage: 'render', progress: 0 } });
      return true;
    } catch (error) {
      toast(errorMessage(error));
      if (error instanceof ApiError && error.code === 'insufficient_balance') dispatch({ type: 'route', screen: 'limit' });
      return false;
    } finally { dispatch({ type: 'busy', value: false }); }
  }, [dispatch, state.project, toast]);

  const remontage = useCallback(async (): Promise<boolean> => {
    const project = state.project;
    if (!project) return false;
    dispatch({ type: 'busy', value: true });
    try {
      await api.remontage(project.id);
      // Добавленные к ролику иллюстрации теперь в проекте, строки загрузки больше не нужны.
      dispatch({ type: 'uploads/clear' });
      void loadProfile();
      dispatch({ type: 'route', screen: null });
      dispatch({ type: 'activity/update', id: project.id, patch: { status: 'transcribing', progress: 0, error: null } });
      dispatch({ type: 'stage/set', stage: { status: 'transcribing', stage: 'transcribe', progress: 0 } });
      telegram.hapticFeedback.impact('medium');
      return true;
    } catch (error) {
      toast(errorMessage(error));
      if (error instanceof ApiError && error.code === 'insufficient_balance') dispatch({ type: 'route', screen: 'limit' });
      return false;
    } finally { dispatch({ type: 'busy', value: false }); }
  }, [dispatch, state.project, toast, loadProfile]);

  const deliver = useCallback(async () => {
    const project = state.project;
    if (!project) return;
    dispatch({ type: 'busy', value: true });
    try {
      await api.deliver(project.id);
      toast(t('lib.actions.sending_started'));
      telegram.hapticFeedback.notification('success');
    } catch (error) {
      toast(errorMessage(error));
    } finally {
      dispatch({ type: 'busy', value: false });
    }
  }, [dispatch, state.project, toast]);

  return useMemo<Actions>(
    () => ({
      bootstrap,
      refreshProjects,
      loadProfile,
      finishOnboarding,
      openProject,
      reloadProject,
      ensureProject,
      deleteProject,
      renameProject,
      addFiles,
      retryUpload,
      moveSource,
      removeUpload,
      removeAsset,
      saveSettings,
      startGeneration,
      applyEdlPatch,
      rerenderCurrent,
      remontage,
      deliver,
      toast,
    }),
    [
      addFiles,
      applyEdlPatch,
      rerenderCurrent,
      remontage,
      bootstrap,
      ensureProject,
      deleteProject,
      renameProject,
      deliver,
      finishOnboarding,
      loadProfile,
      openProject,
      refreshProjects,
      reloadProject,
      removeAsset,
      removeUpload,
      retryUpload,
      moveSource,
      saveSettings,
      startGeneration,
      toast,
    ],
  );
}

/** Сервер может не прислать необязательные поля — приводим к ожидаемой форме. */
function normalize(project: Partial<ProjectDetail> & { id: string }): ProjectDetail {
  return {
    id: project.id,
    format: project.format ?? 'reaction',
    status: project.status ?? 'draft',
    createdAt: project.createdAt ?? new Date().toISOString(),
    expiresAt: project.expiresAt ?? null,
    title: project.title ?? null,
    thumbUrl: project.thumbUrl ?? null,
    durationS: project.durationS ?? null,
    settings: { ...DEFAULT_SETTINGS, ...project.settings },
    assets: project.assets ?? [],
    edl: project.edl ?? null,
    edlVersion: project.edlVersion ?? null,
    renderedEdlVersion: project.renderedEdlVersion ?? null,
    correctionCost: project.correctionCost ?? 0,
    watermarked: project.watermarked ?? false,
    sourcesAvailable: project.sourcesAvailable ?? true,
    candidates: project.candidates ?? [],
    render: project.render ?? null,
    stage: project.stage ?? null,
    progress: project.progress ?? null,
    error: project.error ?? null,
  };
}
